import { Linkedin, Mail, Phone } from "lucide-react";
import { useRecordContext, WithRecord, useTranslate } from "ra-core";
import type { ReactNode } from "react";
import { ArrayField } from "@/components/ds/admin/array-field";
import { EditButton } from "@/components/ds/admin/edit-button";
import { DeleteButton } from "@/components/admin";
import { ReferenceField } from "@/components/ds/admin/reference-field";
import { ReferenceManyField } from "@/components/ds/admin/reference-many-field";
import { ShowButton } from "@/components/ds/admin/show-button";
import { SingleFieldList } from "@/components/ds/admin/single-field-list";
import { TextField } from "@/components/ds/admin/text-field";
import { DateField } from "@/components/ds/admin/date-field";
import { EmailField } from "@/components/ds/admin/email-field";

import { AddTask } from "../tasks/AddTask";
import { TasksIterator } from "../tasks/TasksIterator";
import { TagsListEdit } from "./TagsListEdit";
import { AsideSection } from "../misc/AsideSection";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { SaleName } from "../sales/SaleName";
import type { Contact } from "../types";
import { ContactMergeButton } from "./ContactMergeButton";
import { ExportVCardButton } from "./ExportVCardButton";
import { ContactHealthCard } from "./ContactHealthCard";
import { translateChoice } from "@/i18n/utils";


interface ContactAsideProps {
  link?: "edit" | "show";
}

/**
 * Actions-only strip. Can be rendered outside the aside (e.g. in the mobile
 * header) without pulling in the layout constraints of the full aside.
 */
export const ContactAsideActions = ({ link = "edit" }: ContactAsideProps) => {
  const translate = useTranslate();
  const record = useRecordContext<Contact>();
  if (!record) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {link === "edit" ? (
        <EditButton
          label={translate("crm.contact.action.edit")}
          record={record}
          resource="contacts"
        />
      ) : (
        <ShowButton label={translate("crm.contact.action.show")} />
      )}
      {link !== "edit" && (
        <>
          <ExportVCardButton />
          <ContactMergeButton />
          <DeleteButton
            className="text-destructive hover:bg-destructive/10"
            size="sm"
          />
        </>
      )}
    </div>
  );
};

export const ContactAside = ({ link = "edit" }: ContactAsideProps) => {
  const { contactGender } = useConfigurationContext();
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <div className="hidden sm:block w-64 min-w-64 text-sm">
      <div className="mb-4 -ml-1">
        <ContactAsideActions link={link} />
      </div>

      <ContactHealthCard />
      <AsideSection title={translate("crm.contact.section.personal_info")}>
        {/* ...rest unchanged... */}
      </AsideSection>

      <AsideSection title={translate("crm.contact.section.background_info")}>
        {/* ...rest unchanged... */}
      </AsideSection>

      <AsideSection title={translate("crm.contact.section.tags")}>
        <TagsListEdit />
      </AsideSection>

      <AsideSection title={translate("crm.contact.section.tasks")}>
        <ReferenceManyField
          reference="crm_activities"
          target="contact_id"
          sort={{ field: "due_date", order: "ASC" }}
          filter={{ type: "task", "done_date@is": null }}
        >
          <TasksIterator />
        </ReferenceManyField>
        <AddTask resource="contacts" />
      </AsideSection>

      {/* Actions only render inside the aside when NOT edit — the edit case
          is just the Edit button, already rendered above. */}
      {link !== "edit" && (
        <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
          <DeleteButton
            className="text-destructive hover:bg-destructive/10"
            size="sm"
          />
        </div>
      )}
    </div>
  );
};

// PersonalInfoRow unchanged

const PersonalInfoRow = ({
  icon,
  primary,
  showType,
}: {
  icon: ReactNode;
  primary: ReactNode;
  showType?: boolean;
}) => {
  const translate = useTranslate();
  return (
    <div className="flex flex-row items-center gap-2 min-h-6">
      {icon}
      <div className="flex flex-wrap gap-x-2 gap-y-0">
        {primary}
        {showType ? (
          <WithRecord
            render={(row) =>
              row.type !== "Other" && (
                <span className="text-muted-foreground">
                  {translate(`crm.contact.type.${row.type.toLowerCase()}`, {
                    _: row.type,
                  })}
                </span>
              )
            }
          />
        ) : null}
      </div>
    </div>
  );
};
